import { TuiDialogService, TuiRoot } from '@taiga-ui/core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { defer, filter, fromEvent, switchMap, throwError } from 'rxjs';
import { NAVIGATOR } from '@shared/providers/navigator.provider';
import {
  BroadcastChannelService,
  MessageTypes,
} from '@shared/services/broadcast-channel.service';

import { WINDOW } from '@shared/providers/window.provider';
import { isPlatformBrowser } from '@angular/common';
import { VersionAvailableComponent } from './core/components/version-available/version-available.component';

import { RouteNames } from './route-names';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, TuiRoot, TuiRoot],
  templateUrl: './app.component.html',
  styleUrl: './app.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  title = 'trust-checker';
  readonly WORKER_NOT_FOUND = 'Worker not found. Use HTTPS connection!';
  workerMessage$ = defer(() => {
    const worker = this.navigator?.serviceWorker ?? null;
    return worker
      ? fromEvent<MessageEvent>(worker, 'message')
      : throwError(() => new Error(this.WORKER_NOT_FOUND));
  });

  workerReady$ = defer(() => {
    const worker = this.navigator?.serviceWorker ?? null;
    return worker
      ? fromPromise(worker.ready)
      : throwError(() => new Error(this.WORKER_NOT_FOUND));
  }).pipe(
    filter(s => s !== null),
    takeUntilDestroyed(this.destroyRef),
  );

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: NonNullable<unknown>,
    @Inject(NAVIGATOR) private readonly navigator: Navigator,
    @Inject(WINDOW) private readonly window: Window,
    @Inject(TuiDialogService) private readonly dialogs: TuiDialogService,
    private readonly destroyRef: DestroyRef,
    private readonly swUpdate: SwUpdate,
    private readonly broadcast: BroadcastChannelService,
    private readonly router: Router,
  ) {
    this.syncRoutesWithServiceWorker();
  }

  syncRoutesWithServiceWorker() {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.navigator.serviceWorker?.controller) {
          this.navigator.serviceWorker.controller.postMessage({
            type: 'URL_UPDATED',
            url: this.window.location.href,
          });
        }
      });

    if (this.navigator.serviceWorker?.controller) {
      this.navigator.serviceWorker.controller.postMessage({
        type: 'APP_INSTANCE_CREATED',
      });
    }
  }

  showNotification() {
    return this.dialogs.open(new PolymorpheusComponent(VersionAvailableComponent), {
      size: 's',
      closeable: false,
      dismissible: false,
    });
  }

  checkNewVersion() {
    this.swUpdate?.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        switchMap(() => this.showNotification()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  checkIfDocumentAlreadyOpened() {
    this.workerReady$.subscribe(worker => {
      worker?.active?.postMessage({ type: 'APP_INSTANCE_CREATED' });
    });
  }

  closeWindowIfDocumentAlreadyOpened() {
    this.workerMessage$.subscribe(event => {
      const { type, message } = event.data;
      if (type === 'DOCUMENT_ALREADY_OPENED') {
        this.window.alert(message);
        this.window.close();
      }
    });
  }

  observeBroadcastMessages() {
    this.broadcast.messages$
      .pipe(
        filter(event => event.data.type === MessageTypes.NAVIGATE_LOGIN),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.router.navigate([RouteNames.LOGIN_PAGE]);
      });

    this.broadcast.messages$
      .pipe(
        filter(event => event.data.type === MessageTypes.NAVIGATE_HOME),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.router.navigate([RouteNames.HOME]);
      });
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.checkIfDocumentAlreadyOpened();
      this.closeWindowIfDocumentAlreadyOpened();
      this.checkNewVersion();
      this.observeBroadcastMessages();
    }
  }
}
